import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { fmtMoney } from '../lib/format';

/**
 * Generic "Confirm actual amount" dialog used for estimated items during reconciliation.
 *
 * Props:
 *   item           { description, amount, category }   — pre-fill source
 *   open           boolean
 *   onOpenChange   (open) => void
 *   onConfirm      async ({ amount, description, category }) => void  (parent handles API)
 *   estimatedLabel string shown in the helper line (defaults to "the estimate")
 */
const ConfirmAmountDialog = ({ item, open, onOpenChange, onConfirm, estimatedLabel = 'the estimate' }) => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (item) {
      setAmount(String(item.amount ?? ''));
      setDescription(item.description ?? '');
      setCategory(item.category ?? '');
    }
  }, [item]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!item) return;
    const amt = parseFloat(amount);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm({
        amount: amt,
        description: description.trim() || item.description,
        category: category.trim() || null,
      });
    } catch (err) {
      // Parent should toast on error; this is a safety net only.
      const detail = err.response?.data?.detail;
      toast.error(detail || 'Failed to confirm amount');
    } finally {
      setSubmitting(false);
    }
  };

  if (!item) return null;
  const estimatedAmount = Number(item.amount) || 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="confirm-amount-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#020617]">Confirm actual amount</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#64748B] -mt-2">
          This is flagged as an estimate. Enter the actual figure to reconcile it. {estimatedLabel.charAt(0).toUpperCase() + estimatedLabel.slice(1)} was <span className="font-semibold text-[#020617]">£{fmtMoney(estimatedAmount)}</span>.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="confirm-amount-form">
          <div>
            <Label htmlFor="confirm-amount">Actual amount (GBP)</Label>
            <Input
              id="confirm-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              className="mt-1.5"
              data-testid="confirm-amount-input"
            />
          </div>
          <div>
            <Label htmlFor="confirm-description">Description</Label>
            <Input
              id="confirm-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1.5"
              data-testid="confirm-description-input"
            />
          </div>
          <div>
            <Label htmlFor="confirm-category">Category (Optional)</Label>
            <Input
              id="confirm-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Food, Transport, etc."
              className="mt-1.5"
              data-testid="confirm-category-input"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-[#0052FF] text-white hover:bg-[#003ECC]"
              data-testid="confirm-amount-submit"
            >
              {submitting ? 'Reconciling...' : 'Confirm & reconcile'}
            </Button>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex-1 bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmAmountDialog;
